import React from "react";
import { SecurityCodeMethod } from "../../mfa/hooks/useMfaApi";
import Local from "../classes/local";
import getUrlParameters from "../functions/getUrlParameters";
import translate from "../functions/translate";

export interface TealiumViewData {
    language: 'es' | 'en'
    page_url: string
    page_path: string
    page_name: string
    page_title: string
    site_section: 'accounts' | 'mfa'
    site_division: 'accounts'
    site_tool: 'login'
    step_number: string | null
    step_name: string | null
    site_environment: string
    logged_in: 'false'
    requesting_site_tool: string
    content_type: 'account management'
}

export interface TealiumLinkData {
    event_name?: string
    event_type?: 'ui interaction' | 'conversion' | 'non-interaction'
    error_type?: string | null
    error_code?: string | null
    error_message?: string | null
    text?: string | null
    target_location?: string | null
    heading?: string | null
    component?:
        'account_found' |
        'forgot username and password' |
        'form' |
        'help drawer' |
        'login' |
        'modal' |
        'reset_password' |
        'sign_up' |
        'unlocked_account' |
        'updated_account' |
        'username_reminder' |
        ''
    event_category?: 'accounts' | 'content tools' | 'ui interaction' | 'mfa' | 'login'
    event_action?: string
    event_label?: string
    event_noninteraction?: boolean
    ga_eventCategory?: string
    ga_eventAction?: string
    ga_eventLabel?: string
    ga_eventType?: string
    ga_eventValue?: string
    mfa_type?: 'text' | 'email' | 'phone'
    form_field_label?: string
    link_url?: string
    html_id?: string
    type?: string
    link_text?: string | null
    button_style?: 'default' | 'primary' | 'success' | 'transparent'
    button_type?: 'submit' | 'link' | 'reset' | 'button'
    parent_component_heading?: string
    parent_component_type?: string
    ga_eventNoninteraction?: boolean
    alert_type?: 'default' | 'error' | 'warning' | 'success' | 'lightweight'
    login_method?: 'mfa' | 'sfa',
    form_name?: string | null,
    coverage_type?: 'Part A' | 'Part B' | 'Part B: PBID'
}

const { REACT_APP_DEBUG_ANALYTICS } = process.env;
const LOG_ANALYTICS = REACT_APP_DEBUG_ANALYTICS === 'true';
declare global {
    interface Window {
        utag?: {
            view: (data: TealiumViewData) => void,
            link: (data: TealiumLinkData) => void
        }
        newrelic?: {
            addRelease: (release_name: string, release_id: string) => {}
            noticeError: (err: Error, customAttributes?: Record<string, any>) => {}
        }
    }
}

const logBadge = (type: string ) => `
    background: ${type === 'page_view' ? '#df930a' : 'blue'};
    color: white;
    padding: 3px 10px;
    border-radius: 10px;
    min-width: 50px;
`;

const isViewData = (data: TealiumLinkData | TealiumViewData): data is TealiumViewData => !!(data as TealiumViewData).page_title;

const analyticsLogger =  (data: TealiumLinkData | TealiumViewData) => {
    let type = 'event'
    if (isViewData(data)) {
        type = 'page_view'
    }
    const badge = [`%c${type}`, logBadge(type)];
    const timestamp = new Date().toLocaleTimeString();
    console.groupCollapsed(...badge, timestamp, '|', isViewData(data) ? data.page_title : data.event_name);
    if (isViewData(data)) {
        const { page_name } = data;
        console.log('page_name:', page_name);
    }
    console.table(data);
    console.groupEnd();
};

const withUtag = async (): Promise<Window['utag']> => {
    if (LOG_ANALYTICS) {
        // @ts-ignore
        window.utag.link = analyticsLogger;
        // @ts-ignore
        window.utag.view = analyticsLogger;
    }

    for (let counter = 0; counter < 50; counter++) {
        if (window?.utag?.view) {
            return window.utag;
        }
        await new Promise(res => setTimeout(res, 1000));
    }
};

interface TealiumPageViewData {
    stepNum?: number
    stepName?: string
    siteSection?: TealiumViewData['site_section']
}
export const pageView = async (pageName: string, extra?: TealiumPageViewData) => {
    const language = Local.getItem('language') === 'Spanish' ? 'es' : 'en';
    const location = window.location;
    const params = getUrlParameters();

    const stepNum = extra?.stepNum ? extra.stepNum.toString() : null;
    const stepName = extra?.stepName ?? null;
    const siteSection = extra?.siteSection ?? 'accounts';

    const data: TealiumViewData = {
        // page
        language,
        page_url: location.href,
        page_path: location.pathname,
        page_name: pageName,
        page_title: document.title,
        site_section: siteSection,
        site_tool: 'login',
        step_number: stepNum,
        step_name: stepName,
        site_environment: process.env.REACT_APP_ENV!,
        site_division: 'accounts',
        content_type: 'account management',

        // user
        logged_in: 'false',
        requesting_site_tool: params.get('client_id') || '',
    }

    // Use the resolved utag object from the Promise
    const utag = await withUtag();
    utag?.view(data);
};

export const mfaPageView = (...args: Parameters<typeof pageView>) => {
    let [pageName, extra] = args;
    if (!extra) {
        extra = {};
    }
    extra.siteSection = 'mfa';
    pageView(pageName, extra);
}

const utagLink = async (data: TealiumLinkData) => {
    const utag = await withUtag();
    utag?.link(data);
}

const defaultTealiumData = (): TealiumLinkData => {
    return {
        // event
        event_name: '',
        event_type: 'ui interaction',
        error_type: null,
        error_code: null,
        error_message: null,
        text: null,
        target_location: null,
        heading: null,
        component: '',

        //UA and legacy requirements
        form_name: null,
        event_category: 'accounts',
        event_action: '',
        event_label: '',
        event_noninteraction: true
    }
}

export const helpDrawerOpened = (heading: string, text: string, siteSection?: string) => {
    const data: TealiumLinkData = {
        event_name: 'help_drawer_opened',
        event_type: 'ui interaction',
        heading,
        text,
        event_category: siteSection === 'mfa' ? 'ui interaction' : 'accounts',
        event_action: siteSection === 'mfa' ? 'help drawer opened' : 'help drawer impression',
        event_label: heading
    };
    if(siteSection === 'mfa') {
        data.component = 'help drawer'
    }
    utagLink(data);
}

export const helpDrawerClosed = (heading: string, siteSection?: string) => {
    const data: TealiumLinkData = {
        event_name: 'help_drawer_closed',
        event_type: 'ui interaction',
        heading,
        event_category: siteSection === 'mfa' ? 'ui interaction' : 'accounts',
        event_action: 'help drawer closed',
        event_label: heading
    };
    if(siteSection !== 'mfa') {
        data.component = 'help drawer'
    }
    utagLink(data);
}

export const modalImpression = (heading: string) => {
    const data = Object.assign<TealiumLinkData, Partial<TealiumLinkData>>(defaultTealiumData(), {
        event_name: 'modal_impression',
        heading,
        component: 'modal',
        event_action: 'modal impression',
        event_label: heading,
        event_category: 'content tools'
    });

    utagLink(data);
};

export const formStart = (formName: string) => {
    const data: TealiumLinkData = {
        event_category: "accounts",
        event_action: formName,
        event_label: 'start form',
        //Supports GA4
        event_name: 'form_start',
        event_type: 'ui interaction',
        form_name: formName,
        component: "form"
    };

    utagLink(data);
};

const mfaTypeToBlastMfaType: Record<SecurityCodeMethod, TealiumLinkData['mfa_type']> = {
    email: 'email',
    sms: 'text',
    voice: 'phone'
};

export const mfaSetupStart = (mfaType: SecurityCodeMethod) => {
    const type = mfaTypeToBlastMfaType[mfaType];
    const data: TealiumLinkData = {
        mfa_type: type,
        event_type: 'conversion',
        event_name: 'mfa_setup_start',
        event_category: 'mfa',
        event_action: 'setup',
        event_label: `mfa: ${type}: start`
    };
    utagLink(data);
}

export const mfaSetupSuccess = (mfaType: SecurityCodeMethod) => {
    const type = mfaTypeToBlastMfaType[mfaType];
    const data: TealiumLinkData = {
        mfa_type: type,
        event_type: 'conversion',
        event_name: 'mfa_setup_success',
        event_category: 'mfa',
        event_action: 'setup',
        event_label: `mfa: ${type}: success`
    };
    utagLink(data);
};

interface InternalLinkClickedData {
    linkText: string
    linkUrl?: string
    parentComponentHeading: string
    parentComponentType?: 'app page' | 'footer'
    siteSection?: 'mfa' | 'account management'
}
export const internalLinkClicked = (linkData: InternalLinkClickedData) => {
    const {
        linkText,
        linkUrl,
        parentComponentHeading,
        parentComponentType = 'app page',
        siteSection
    } = linkData;
    const data: TealiumLinkData = {
        link_text: linkText,
        link_url: linkUrl,
        parent_component_heading: parentComponentHeading,
        parent_component_type: parentComponentType,
        event_name: "internal_link_clicked",
        event_type: 'ui interaction',
        event_category: siteSection === 'mfa' ? 'ui interaction' : 'accounts',
        event_action: siteSection === 'mfa' ? 'internal link clicked' : 'link clicked',
        event_label: siteSection === 'mfa' ? `${linkText}: ${linkUrl}` :`${linkUrl}`
    }
    utagLink(data);
}

interface AlertImpressionData {
    heading: string
    alertType: TealiumLinkData['alert_type']
    text: string
    errorCode?: string
}
export const alertImpression = (alertData: AlertImpressionData) => {
    const {
        heading,
        alertType,
        text,
        errorCode
    } = alertData;
    const data: TealiumLinkData = {
        heading,
        alert_type: alertType,
        text,
        event_name: 'alert_impression',
        event_type: 'non-interaction',
        error_code: errorCode,
        error_message: null,
        error_type: 'system',
        event_category: 'content tools',
        event_action: 'alert impression',
        event_label: `${heading}: ${text}`
    };
    utagLink(data);
}

export const loginSuccessEvent = (isMfaLogin: boolean) => {
    const method = isMfaLogin ? 'mfa' : 'sfa';
    const data: TealiumLinkData = {
        login_method: method,
        event_name: 'login',
        event_category: 'login',
        event_action: 'success',
        event_label: `login method: ${method}`
    };
    utagLink(data);
}

export interface MfaButtonEngagementData {
    text: string
    buttonStyle: TealiumLinkData['button_style']
    buttonType: TealiumLinkData['button_type'],
    parentComponentHeading: string
    parentComponentType?: 'app page'
    linkUrl?: string
    siteSection?: 'mfa' | 'account management'
}
export const mfaButtonEngagement = (buttonData: MfaButtonEngagementData) => {
    const {
        text,
        buttonStyle,
        buttonType,
        parentComponentHeading,
        parentComponentType = 'app page',
        linkUrl,
        siteSection
    } = buttonData;
    const data: TealiumLinkData = {
        text,
        button_style: buttonStyle,
        button_type: buttonType,
        parent_component_heading: parentComponentHeading,
        parent_component_type: parentComponentType,
        link_url: linkUrl,
        event_name: 'button_engagement',
        event_type: 'ui interaction',
        event_category: siteSection === 'mfa' ? 'ui interaction' : 'accounts',
        event_action: siteSection === 'mfa' ? `engaged ${buttonStyle} button` : 'button clicked',
        event_label: siteSection === 'mfa' ? `button text: ${text}` : text
    }
    utagLink(data);
}

export interface MfaInlineErrorData {
    errorMessage: string
    errorCode: string | null
    errorType: 'validation' | 'system'
    fieldLabel: string
}
export const mfaInlineError = (errData: MfaInlineErrorData) => {
    const data: TealiumLinkData = {
        event_name: 'inline_error',
        event_type: 'non-interaction',
        error_code: errData.errorCode,
        error_message: errData.errorMessage,
        error_type: errData.errorType,
        form_field_label: errData.fieldLabel,
        event_category: 'content tools',
        event_action: 'inline error',
        event_label: errData.errorMessage
    }
    utagLink(data);
}

/** onClick handler for <a></a> to track analytics */
export const trackLinkClick = (parentComponentHeading: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!e.currentTarget) {
        return;
    }
    const el = e.currentTarget;
    internalLinkClicked({
        linkText: (el.textContent || '').trim(),
        linkUrl: el.href,
        parentComponentHeading,
        siteSection: 'account management'
    });
};

const sharedConversionEventData = (component: TealiumLinkData['component'], conversionName: TealiumLinkData['component'], status = ''): TealiumLinkData => {
    let event_name: string | undefined = conversionName;
    if (status) {
        event_name = [conversionName, status].join('_');
    }
    return Object.assign<TealiumLinkData, Partial<TealiumLinkData>>(defaultTealiumData(), {
        event_category: 'accounts', //for login its login
        event_action: conversionName,
        event_name,
        event_type: 'ui interaction',
        event_label: 'attempted',
        component,     // login, create account, forgot password, etc. (friendlier name of conversion name)
    });
};

export const conversionAttempt = (conversionName: TealiumLinkData['component'], coverageType?: TealiumLinkData['coverage_type']) => {
    const status = 'attempted';
    const data: TealiumLinkData = {
        //supports UA
        event_category: "accounts",
        event_action: conversionName,
        event_label: "attempted",
        //Supports GA4
        event_name: [conversionName, status].join('_'),
        event_type: "ui interaction",
    };
    if (coverageType) {
        data.coverage_type = coverageType;
    }

    utagLink(data);
};

export const conversionSuccess = (component: TealiumLinkData['component'], coverageType?: TealiumLinkData['coverage_type']) => {
    let eventName = component;
    if (component === 'sign_up') { // this is the only succeeded event with `succeeded` in the event name
        eventName += '_succeeded';
    }
    const data: TealiumLinkData = {
        event_category: "accounts",
        event_action: component,
        event_label: "succeeded",
        //Supports GA4
        event_name: eventName,
        event_type: "conversion"
    };
    if (coverageType) {
        data.coverage_type = coverageType;
    }
    utagLink(data);
}

/** Multi step processes should not have "succeeded" or "failed" event labels - just "error" if there is an error */
export const conversionIntermediateFailure = (component: TealiumLinkData['component'], conversionName: TealiumLinkData['component'], errorType: string, errorMessage: string) => {
    const status = 'error';
    const data = Object.assign<TealiumLinkData, Partial<TealiumLinkData>>(sharedConversionEventData(component, conversionName, status), {
        error_type: errorType,
        error_message: errorMessage,
        event_label: status
    });

    utagLink(data);
}

export const conversionFailure = (component: TealiumLinkData['component'], conversionName: TealiumLinkData['component'], errorType: string, errorMessage: string) => {
    const status = 'failed';
    const data = Object.assign<TealiumLinkData, Partial<TealiumLinkData>>(sharedConversionEventData(component, conversionName, status), {
        error_type: errorType,
        error_message: errorMessage,
        event_label: status
    });

    utagLink(data);
}
